import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useRequest } from '@frontend/api';
import type { MutationFunction, MutationFunctionContext } from '@frontend/api';
import { toSnakeCase } from '@frontend/utils';

import {
  getMultisiteDashboardUserSettingsMutationKey,
  getMultisiteDashboardUserSettingsQueryKey,
} from './keys';
import type {
  MultisiteDashboardUserSettingsData,
  UseMultisiteDashboardUserSettingsParams,
} from './types';

const toWireFormat = (
  data: MultisiteDashboardUserSettingsData,
): MultisiteDashboardUserSettingsData => ({
  quotesOverviewVisibleColumns:
    data.quotesOverviewVisibleColumns?.map(toSnakeCase) ?? null,
});

export const useUpdateMultisiteDashboardUserSettings: MutationFunction<
  MultisiteDashboardUserSettingsData,
  MultisiteDashboardUserSettingsData,
  [UseMultisiteDashboardUserSettingsParams]
> = (params, { requestOptions, queryOptions = {} } = {}) => {
  const queryClient = useQueryClient();

  const { userId } = params;
  const urlPath = `/users/${userId}/multisite_dashboard_user_settings`;
  const mutationKey = getMultisiteDashboardUserSettingsMutationKey(params);
  const queryKey = getMultisiteDashboardUserSettingsQueryKey(params);
  const request = useRequest(requestOptions);

  return useMutation<
    MultisiteDashboardUserSettingsData,
    unknown,
    MultisiteDashboardUserSettingsData,
    MutationFunctionContext<MultisiteDashboardUserSettingsData>
  >({
    mutationFn: (requestData) => {
      return request(urlPath, {
        init: {
          method: 'PATCH',
          body: toWireFormat(requestData),
        },
      });
    },
    onMutate: (requestData) => {
      const prev =
        queryClient.getQueryData<MultisiteDashboardUserSettingsData>(queryKey);
      queryClient.setQueryData(queryKey, toWireFormat(requestData));
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(queryKey, context.prev);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
    ...queryOptions,
    mutationKey,
  });
};
